import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import AutoPayIcon from '@assets/icons/autopay.svg';
import i18n from '@/utils/i18n';
import MenuHorizontalIcon from '@assets/icons/menu-horizontal.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Action } from '@/components/navigation-header-content';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Analytics } from '@/utils/analytics';
import { ApolloError } from '@apollo/client';
import {
  APP_CONTAINER_ROUTES,
  APP_MODAL_ROUTES,
  PAYMENT_ROUTES,
} from '@/navigators/routes';
import {
  AppContainerStackParams,
  AppModalStackParams,
  PaymentsStackParams,
} from '@/navigators/types';
import { BankDepositsContent } from '@/components/payments/bank-deposits-content';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { CompositeScreenProps, useIsFocused } from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { CreditRequestInvoiceStoreProvider } from '@/stores/credit-request-invoice-store';
import { getMessageFromGQLError } from '@/utils/apollo-client';
import { Linking, RefreshControl, View } from 'react-native';
import { List } from '@/components/list';
import { NewAutoPayIcon } from './new-auto-pay-icon';
import {
  NewFeature,
  PaymentItemType,
  PaymentMethodType,
  PaymentsStatus,
} from '@/enums';
import { PageContainer } from '@/components/page-container';
import { partnerSupplier } from '@/utils/environment';
import { PaymentBannerContainer } from '@/components/payments/payment-banner-container';
import { PaymentHistoryComponent } from './payment-history-component';
import { PaymentHistoryListItem } from '@/components/payments/payment-history-list-item';
import { PaymentLink } from '@/components/payments/payment-links-list';
import { PaymentLinksComponent } from './payment-links-component';
import { PaymentLinksListItem } from '@/components/payments/payment-links-list-item';
import {
  PaymentsActionListOption,
  usePaymentsScreenMoreActions,
} from '@/hooks/payments/use-payments-screen-more-actions';
import { StackScreenProps } from '@react-navigation/stack';
import { TabCardDetails } from '@/components/payments/types';
import { TabCardEnrollmentBanner } from '@/components/payments/tab-card-enrollment-banner';
import { toUnsavedPaymentMethodOption } from '@/utils/payments';
import { Typography } from '@usepepper/ui/atoms/typography';
import { UnpaidInvoiceComponent } from '@/components/payments/unpaid-invoice-component';
import { UnpaidInvoiceListItem } from '@/components/payments/unpaid-invoice-list-item';
import { UnpaidInvoiceTotalComponent } from '@/components/payments/unpaid-invoice-total-component';
import {
  UnpaidItemType,
  usePaymentsContext,
} from '@/contexts/payments-context';
import {
  useAuthenticatedUserContext,
  useUserContext,
} from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useGenerateStatement } from '@/hooks/payments/use-generate-statement';
import { useGetPaymentLinks } from '@/hooks/payments/use-get-payment-links';
import { useHeaderRestaurantSelectorProps } from '@/components/use-header-restaurant-selector-props';
import { useNewFeatureIndicator } from '@/hooks/use-new-feature-indicator';
import { usePayCapability } from '@/hooks/payments/use-pay-capability';
import { useTabCardBanner } from '@/hooks/payments/use-tab-card-banner';
import { useToasts } from '@/contexts/toast-context';

export enum PaymentTabRoutes {
  BankDeposits = 'BankDeposits',
  History = 'History',
  PaymentLinks = 'PaymentLinks',
  Unpaid = 'Unpaid',
}

export interface TabRoute {
  key: PaymentTabRoutes;
  title: string;
}

type Props = CompositeScreenProps<
  CompositeScreenProps<
    CompositeScreenProps<
      StackScreenProps<PaymentsStackParams, PAYMENT_ROUTES.Base>,
      StackScreenProps<
        AppContainerStackParams,
        APP_CONTAINER_ROUTES.PaymentDetail
      >
    >,
    StackScreenProps<AppContainerStackParams, APP_CONTAINER_ROUTES.OrderDetail>
  >,
  StackScreenProps<AppModalStackParams>
>;

/**
 * Mobile payments screen
 *
 * Note: when making changes to this screen ensure that `split-payments-screen`
 * is also updated or the mobile and web experiences will diverge from one
 * another.
 */
export const PaymentsScreen: React.FC<Props> = ({ navigation, route }) => {
  const {
    supplierEmployeeRole,
    selectedRestaurantRelationship: {
      appPaymentsEnabled,
      employeeRole,
      onDemandPaymentStatementEnabled,
      supplierPaymentsStatus,
      showPaymentLinks,
    },
    user,
  } = useAuthenticatedUserContext();
  const {
    allUnpaidItems,
    autoPayEnabled,
    autoShowModal,
    currencyCode,
    enabledPaymentMethods,
    ftniEnabled,
    loading: paymentsContextLoading,
    paymentMethods,
    plaidEnabled,
    setPaymentContextState,
    useV2Invoices,
  } = usePaymentsContext();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const isSupplierEmployee = Boolean(supplierEmployeeRole);
  const { canRecordCashPayment, canRecordCheckPayment, isDistributorUser } =
    usePayCapability({
      enabledPaymentMethods,
      restaurantEmployeeRole: employeeRole,
      showPaymentLinks,
      supplierEmployeeRole,
    });
  // Bank deposits are where a distributor employee banks what they collected
  // in person, so it follows the same flags as recording cash and checks.
  const showBankDeposits =
    isDistributorUser && (canRecordCashPayment || canRecordCheckPayment);
  const isFocused = useIsFocused();
  const baseHeaderProps = useHeaderRestaurantSelectorProps();
  const { showErrorToast } = useToasts();
  const [styles, theme] = useStyles();

  const rawTabCardConfig = useDynamicConfigValue('tab_card_config');
  const tabCardDetails: TabCardDetails | null =
    rawTabCardConfig?.enabled &&
    rawTabCardConfig?.sign_up_url &&
    rawTabCardConfig?.cashback_percentage !== null &&
    rawTabCardConfig?.cashback_percentage !== undefined
      ? {
          cashbackPercentage: rawTabCardConfig.cashback_percentage,
          distributorName: partnerSupplier.fullName,
          ...(rawTabCardConfig?.image_url && {
            imageUrl: rawTabCardConfig.image_url,
          }),
          signUpUrl: rawTabCardConfig.sign_up_url,
        }
      : null;

  const { dismissBanner: dismissTabBanner, isDismissed: isTabBannerDismissed } =
    useTabCardBanner(restaurantUUID);
  const hasTabPaymentMethod = paymentMethods.some(
    (pm) => pm.type === PaymentMethodType.TAB,
  );
  const showTabBanner =
    Boolean(tabCardDetails) &&
    !isTabBannerDismissed &&
    !hasTabPaymentMethod &&
    Boolean(employeeRole || supplierEmployeeRole);

  const [selectedTab, setSelectedTab] = useState(
    appPaymentsEnabled ? PaymentTabRoutes.Unpaid : PaymentTabRoutes.History,
  );
  const [showMoreActionsSheet, setShowMoreActionsSheet] = useState(false);
  const lastUnsavedPaymentMethodRef = useRef<string>(null);
  const [generateStatement] = useGenerateStatement();
  const unsavedPaymentMethodJson = route?.params?.unsavedPaymentMethodJson;

  const {
    loading: paymentLinksLoading,
    paymentLinks,
    refresh,
    refreshing,
  } = useGetPaymentLinks(
    restaurantUUID,
    supplierUUID,
    !isSupplierEmployee || !showPaymentLinks,
  );

  /*
    unsavedPaymentMethodJson is only passed from the add payment method
    modal. This value is temporarily stored in the payments context as
    the selected payment method. This value will not be reset by the ref
    and will only update when new payment methods are added but not saved.
  */
  useEffect(() => {
    if (
      unsavedPaymentMethodJson &&
      lastUnsavedPaymentMethodRef.current !== unsavedPaymentMethodJson
    ) {
      const adyenData = JSON.parse(unsavedPaymentMethodJson);
      setPaymentContextState({
        selectedPaymentMethod: toUnsavedPaymentMethodOption(adyenData),
      });
      lastUnsavedPaymentMethodRef.current = unsavedPaymentMethodJson;
    }
  }, [setPaymentContextState, unsavedPaymentMethodJson]);

  useEffect(() => {
    if (autoShowModal && isFocused) {
      setSelectedTab(PaymentTabRoutes.Unpaid);
    }
  }, [autoShowModal, isFocused]);

  const onPaymentSelect = useCallback(
    (paymentBatchUUID: string) => {
      navigation.navigate(APP_CONTAINER_ROUTES.PaymentDetail, {
        paymentBatchUUID,
      });
    },
    [navigation],
  );

  const onUnpaidItemSelect = useCallback(
    (unpaidItem: UnpaidItemType) => {
      if (unpaidItem.type === PaymentItemType.Invoice) {
        if (useV2Invoices) {
          navigation.navigate(APP_CONTAINER_ROUTES.InvoiceDetail, {
            invoiceUUID: unpaidItem.uuid,
          });
        } else {
          navigation.navigate(APP_CONTAINER_ROUTES.OrderDetail, {
            // orderUUID is required for suppliers not on v2 invoices
            orderUUID: unpaidItem.orderUUID!,
          });
        }
      } else {
        navigation.navigate(APP_CONTAINER_ROUTES.CreditMemoDetail, {
          creditMemoUUID: unpaidItem.uuid,
        });
      }
    },
    [navigation, useV2Invoices],
  );

  const onPaymentLinkSelect = useCallback(
    (paymentLink: PaymentLink) => {
      navigation.navigate(APP_CONTAINER_ROUTES.PaymentLinkDetail, {
        paymentLinkUUID: paymentLink.id,
        restaurantUUID: restaurantUUID,
        supplierUUID: supplierUUID,
      });
    },
    [navigation, restaurantUUID, supplierUUID],
  );

  useEffect(() => {
    // Show order details when user navigates to this screen with an UnpaidItem,
    // such as in OrderSubmittedActionSheet
    if (route.params?.unpaidInvoice) {
      onUnpaidItemSelect(route.params.unpaidInvoice);
    }
  }, [onUnpaidItemSelect, route.params?.unpaidInvoice]);

  // Disable history tab if distributor uses FTNI for payments
  const showHistoryTab = !ftniEnabled;

  const displayWallet =
    supplierPaymentsStatus === PaymentsStatus.Enabled &&
    (appPaymentsEnabled || plaidEnabled) &&
    !ftniEnabled;

  const { showNewIndicator, clearNewIndicator } = useNewFeatureIndicator(
    NewFeature.AUTOPAY,
  );

  const onWalletPress = useCallback(() => {
    Analytics.track('payments_screen.wallet.press');
    navigation.push(PAYMENT_ROUTES.PaymentMethod, {
      requireSavedPaymentMethod: true,
    });
  }, [navigation]);

  const onViewStatementPress = useCallback(async () => {
    Analytics.track('payments_screen.view_statement.press');
    try {
      const response = await generateStatement({
        variables: {
          employeeUUID: user.uuid,
          restaurantUUID,
          supplierUUID,
        },
      });
      if (response.data?.generatePaymentStatementLink) {
        Linking.openURL(
          response.data.generatePaymentStatementLink.payment_statement_url,
        );
      }
    } catch (e) {
      if (e instanceof ApolloError && getMessageFromGQLError(e)) {
        showErrorToast(getMessageFromGQLError(e));
      } else {
        showErrorToast();
      }
    }
  }, [
    generateStatement,
    restaurantUUID,
    showErrorToast,
    supplierUUID,
    user.uuid,
  ]);

  const onBankDepositsPress = () => {
    Analytics.track('payments_screen.bank_deposits.press');
    setSelectedTab(PaymentTabRoutes.BankDeposits);
  };

  const onTransactionHistoryPress = () => {
    Analytics.track('payments_screen.transaction_history.press');
    setSelectedTab(PaymentTabRoutes.History);
  };

  const onPaymentLinksPress = () => {
    Analytics.track('payments_screen.payment_links.press');
    setSelectedTab(PaymentTabRoutes.PaymentLinks);
  };

  const unpaidActions = usePaymentsScreenMoreActions({
    onBankDepositsPress: showBankDeposits ? onBankDepositsPress : null,
    onPaymentLinksPress:
      showPaymentLinks && supplierEmployeeRole !== null
        ? onPaymentLinksPress
        : null,
    onTransactionHistoryPress: showHistoryTab
      ? onTransactionHistoryPress
      : null,
    onViewStatementPress: onDemandPaymentStatementEnabled
      ? onViewStatementPress
      : null,
    onWalletPress: displayWallet ? onWalletPress : null,
  });

  const historyOnlyActions = usePaymentsScreenMoreActions({
    onBankDepositsPress: showBankDeposits ? onBankDepositsPress : null,
    onViewStatementPress: onDemandPaymentStatementEnabled
      ? onViewStatementPress
      : null,
    onWalletPress: displayWallet ? onWalletPress : null,
  });

  const moreActions = appPaymentsEnabled ? unpaidActions : historyOnlyActions;

  const isUnpaidTab = selectedTab === PaymentTabRoutes.Unpaid;

  const actions = useMemo(() => {
    // Hide header actions on history/payment links tabs
    if (!isUnpaidTab && appPaymentsEnabled) {
      return [];
    }

    let headerActions: Action[] = [];

    // Display autopay icon if autopay is enabled and user is a restaurant employee
    if (autoPayEnabled && employeeRole) {
      headerActions.push({
        Icon: showNewIndicator ? NewAutoPayIcon : AutoPayIcon,
        onPress: () => {
          if (showNewIndicator) {
            clearNewIndicator();
          }
          navigation.push(PAYMENT_ROUTES.AutoPay);
        },
        testID: showNewIndicator ? 'new-autopay-icon' : 'autopay-icon',
      });
    }

    if (moreActions.length > 0) {
      headerActions.push({
        Icon: MenuHorizontalIcon,
        onPress: () => {
          Analytics.track('payments_screen.menu_button.press');
          setShowMoreActionsSheet(true);
        },
        testID: 'payment-screen-menu-button',
      });
      return headerActions;
    }

    return headerActions;
  }, [
    appPaymentsEnabled,
    autoPayEnabled,
    clearNewIndicator,
    employeeRole,
    isUnpaidTab,
    moreActions,
    navigation,
    showNewIndicator,
  ]);

  const title = isUnpaidTab ? (
    <Typography variant="paragraph-base">{i18n.t('total_balance')}</Typography>
  ) : selectedTab === PaymentTabRoutes.BankDeposits ? (
    <Typography variant="heading-sm">
      {i18n.t('bank_deposits.title')}
    </Typography>
  ) : selectedTab === PaymentTabRoutes.History ? (
    <Typography variant={appPaymentsEnabled ? 'heading-sm' : 'heading-lg'}>
      {i18n.t('transaction_history')}
    </Typography>
  ) : (
    <Typography variant="heading-sm">{i18n.t('payment_links')}</Typography>
  );

  return (
    <PageContainer
      navigationHeaderProps={{
        ...baseHeaderProps,
        actions,
        ...(isUnpaidTab
          ? {
              contentContainerStyle: [
                baseHeaderProps.contentContainerStyle,
                { backgroundColor: theme.colors.white },
              ],
              contentStyle: [baseHeaderProps.contentStyle, { marginBottom: 0 }],
              showShadow: false,
            }
          : {
              ...(appPaymentsEnabled
                ? {
                    BackButtonIcon: ArrowLeftThin,
                    centerTitle: true,
                    onBackPress: () => setSelectedTab(PaymentTabRoutes.Unpaid),
                    showBackButton: true,
                  }
                : {}),
              showShadow: false,
            }),
        title,
      }}
      statusBarColor={theme.colors.white}
    >
      <View style={styles.base}>
        {selectedTab === PaymentTabRoutes.Unpaid ? (
          <>
            {paymentsContextLoading ? null : (
              <View style={styles.unpaidHeader}>
                <UnpaidInvoiceTotalComponent
                  currencyCode={currencyCode}
                  items={allUnpaidItems}
                />
                {showTabBanner && tabCardDetails ? (
                  <TabCardEnrollmentBanner
                    onDismiss={async () => {
                      const dismissCount = await dismissTabBanner();
                      Analytics.track(
                        'unpaid_invoices.tab_card_banner_dismiss_button.press',
                        { dismissCount },
                      );
                    }}
                    onPress={() => {
                      Analytics.track('unpaid_invoices.tab_card_banner.press');
                      navigation.navigate(
                        APP_MODAL_ROUTES.TabCardApplication,
                        tabCardDetails,
                      );
                    }}
                    tabCardDetails={tabCardDetails}
                  />
                ) : null}
              </View>
            )}
            <PaymentBannerContainer />
            <CreditRequestInvoiceStoreProvider>
              <UnpaidInvoiceComponent
                onPaymentSuccessAutoPayEnrollment={() => {
                  navigation.navigate(PAYMENT_ROUTES.AutoPay);
                }}
                onReviewBannerPress={() => {
                  if (tabCardDetails) {
                    navigation.navigate(
                      APP_MODAL_ROUTES.TabCardApplication,
                      tabCardDetails,
                    );
                  }
                }}
                renderListItem={({ isChecked, onCheck, unpaidItem }) => (
                  <UnpaidInvoiceListItem
                    currencyCode={currencyCode}
                    isChecked={isChecked}
                    onCheck={onCheck}
                    onPress={() => onUnpaidItemSelect(unpaidItem)}
                    showCheckbox={
                      !!employeeRole ||
                      (!!supplierEmployeeRole &&
                        !employeeRole &&
                        showPaymentLinks)
                    }
                    unpaidItem={unpaidItem}
                  />
                )}
                reviewBanner={({ jumpToTabCardApplication }) =>
                  showTabBanner && tabCardDetails ? (
                    <TabCardEnrollmentBanner
                      onDismiss={async () => {
                        const dismissCount = await dismissTabBanner();
                        Analytics.track(
                          'payment_review.tab_card_banner_dismiss_button.press',
                          { dismissCount },
                        );
                      }}
                      onPress={() => {
                        Analytics.track('payment_review.tab_card_banner.press');
                        jumpToTabCardApplication();
                      }}
                      tabCardDetails={tabCardDetails}
                    />
                  ) : null
                }
              />
            </CreditRequestInvoiceStoreProvider>
          </>
        ) : selectedTab === PaymentTabRoutes.BankDeposits ? (
          <BankDepositsContent />
        ) : selectedTab === PaymentTabRoutes.PaymentLinks ? (
          paymentLinksLoading && !refreshing ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator />
            </View>
          ) : (
            <PaymentLinksComponent
              paymentLinks={paymentLinks}
              refreshControl={
                <RefreshControl onRefresh={refresh} refreshing={refreshing} />
              }
              renderListItem={(paymentLink) => (
                <PaymentLinksListItem
                  currencyCode={currencyCode}
                  onPress={() => onPaymentLinkSelect(paymentLink)}
                  paymentLink={paymentLink}
                />
              )}
            />
          )
        ) : (
          <PaymentHistoryComponent
            renderListItem={(payment) => (
              <PaymentHistoryListItem
                currencyCode={currencyCode}
                onPress={() => onPaymentSelect(payment.uuid)}
                payment={payment}
              />
            )}
          />
        )}
      </View>
      {showMoreActionsSheet && moreActions.length > 0 ? (
        <BaseActionSheet
          contentStyle={styles.actionsContentStyle}
          isVisible
          onClose={() => setShowMoreActionsSheet(false)}
        >
          <List<PaymentsActionListOption>
            data={moreActions}
            onSelect={async (item) => {
              await item.metadata?.onPress();
              setShowMoreActionsSheet(false);
            }}
            renderLeftComponent={(item) => {
              const { Icon, iconProps } = item.metadata!;
              return <Icon height={24} width={24} {...iconProps} />;
            }}
          />
        </BaseActionSheet>
      ) : null}
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  actionsContentStyle: {
    paddingHorizontal: 0,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  base: {
    backgroundColor: theme.sk.color.bg.primary.default,
    flex: 1,
  },
  loadingContainer: {
    marginVertical: theme.sk.spacing.spaceLarge,
  },
  unpaidHeader: {
    gap: theme.sk.spacing.spaceMedium,
    marginBottom: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
}));
